import React from 'react';
import { motion } from 'framer-motion';

const LogoType = ({ className = '', size = 'default' }) => {
  // Configuration des tailles avec des valeurs ajustées
  const sizes = {
    small: 'text-lg',
    default: 'text-2xl',
    large: 'text-3xl',
    xlarge: 'text-4xl'
  };

  // Animation des lettres améliorée
  const letterVariants = {
    initial: { y: 0 },
    hover: i => ({
      y: -4,
      transition: {
        duration: 0.3,
        ease: [0.43, 0.13, 0.23, 0.96], // Courbe d'animation personnalisée
        delay: i * 0.05 // Effet cascade
      }
    })
  };

  // Animation du soulignement améliorée
  const underlineVariants = {
    initial: { 
      scaleX: 0,
      opacity: 0 
    },
    hover: { 
      scaleX: 1,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  // Effet de brillance
  const shineVariants = {
    initial: { x: '-100%' },
    hover: {
      x: '100%',
      transition: {
        duration: 1,
        ease: "easeInOut",
        repeat: Infinity,
        repeatDelay: 2
      }
    }
  };

  return (
    <motion.div
      className={`relative inline-flex items-center group ${className}`}
      whileHover="hover"
      initial="initial"
    >
      {/* Container principal avec ombre portée améliorée */}
      <div className={`font-heading font-bold ${sizes[size]} tracking-tight relative`}>
        {/* Première partie du logo */}
        <span className="text-primary-600">
          {['B', 'r', 'i'].map((letter, index) => (
            <motion.span
              key={index}
              custom={index}
              variants={letterVariants}
              className="inline-block relative"
              style={{ 
                textShadow: '0 2px 8px rgba(46, 144, 255, 0.2)'
              }}
            >
              {letter}
            </motion.span>
          ))}
        </span>
        
        {/* Deuxième partie du logo */}
        <span className="text-neutral-800">
          {['e', 'v', 'e', 'n', 't', 's'].map((letter, index) => (
            <motion.span
              key={index}
              custom={index + 3}
              variants={letterVariants}
              className="inline-block"
              style={{ 
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              {letter}
            </motion.span>
          ))}
        </span>

        {/* Effet de brillance */}
        <motion.div
          variants={shineVariants}
          className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent"
          style={{ clipPath: 'polygon(0 0, 10% 0, 30% 100%, 0% 100%)' }}
        />
      </div>

      {/* Effet de soulignement animé amélioré */}
      <motion.div
        variants={underlineVariants}
        className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-primary-600 via-primary-400 to-primary-300"
        style={{ 
          originX: 0,
          boxShadow: '0 2px 4px rgba(46, 144, 255, 0.2)'
        }}
      />

      {/* Point décoratif amélioré */}
      <motion.span 
        className="absolute -right-2 top-0 w-2 h-2 rounded-full bg-gradient-to-br from-primary-400 to-primary-600"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.8, 1, 0.8],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          boxShadow: '0 0 10px rgba(46, 144, 255, 0.4)'
        }}
      />
    </motion.div>
  );
};

export default LogoType; 